export type LogAttributes = Record<string, unknown>;

export interface Logger {
  debug(...messages: unknown[]): void;

  info(...messages: unknown[]): void;

  warn(...messages: unknown[]): void;

  error(...messages: unknown[]): void;

  dir(data: unknown): void;

  exception(exception: unknown): void;
}

type SentryLogLevel = "debug" | "info" | "warn" | "error";

const DEFAULT_LOGGER_NAME = "App";
const SENTRY_LOGS_ENABLED = false;

export function log(name: string): Logger {
  return new ConsoleLogger(name);
}

export function sntLog(name: string): Logger {
  return log(name);
}

export function logInfo(...messages: unknown[]): void {
  log(DEFAULT_LOGGER_NAME).info(...messages);
}

export function logDebug(...messages: unknown[]): void {
  log(DEFAULT_LOGGER_NAME).debug(...messages);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") {
    return false;
  }

  if (Array.isArray(value)) {
    return false;
  }

  if (value instanceof Error) {
    return false;
  }

  if (value instanceof Date) {
    return false;
  }

  return true;
}

function toSentryAttributeValue(value: unknown): string | number | boolean {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (value instanceof Error) {
    return value.message;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function buildMessage(messages: unknown[]): string {
  if (messages.length === 0) {
    return "";
  }

  const first: unknown = messages[0];

  if (typeof first === "string") {
    return first;
  }

  return messages
    .map((message: unknown) => toSentryAttributeValue(message))
    .join(" ");
}

function buildSentryAttributes(
  loggerName: string,
  messages: unknown[]
): Record<string, string | number | boolean> {
  const attributes: Record<string, string | number | boolean> = {
    logger: loggerName
  };

  let parameterMessages: unknown[] = messages;
  const lastMessage: unknown = messages[messages.length - 1];

  if (messages.length > 1 && isPlainObject(lastMessage)) {
    parameterMessages = messages.slice(0, -1);

    for (const [key, value] of Object.entries(lastMessage)) {
      attributes[key] = toSentryAttributeValue(value);
    }
  }

  parameterMessages.forEach((message: unknown, index: number) => {
    if (index === 0 && typeof message === "string") {
      return;
    }

    attributes[`message_parameter_${index}`] = toSentryAttributeValue(message);
  });

  return attributes;
}

function sendSentryLog(
  level: SentryLogLevel,
  loggerName: string,
  messages: unknown[]
): void {
  if (!SENTRY_LOGS_ENABLED) {
    return;
  }

  const message: string = buildMessage(messages);

  if (!message) {
    return;
  }

  const attributes: Record<string, string | number | boolean> =
    buildSentryAttributes(loggerName, messages);

  switch (level) {
    case "debug":
      //Sentry.logger.debug(message, attributes);
      break;
    case "info":
      //Sentry.logger.info(message, attributes);
      break;
    case "warn":
      //Sentry.logger.warn(message, attributes);
      break;
    case "error":
      //Sentry.logger.error(message, attributes);
      break;
  }
}

class ConsoleLogger implements Logger {
  constructor(private readonly name: string) {}

  info(...messages: unknown[]): void {
    console.info(`[${this.name}]`, ...messages);
    sendSentryLog("info", this.name, messages);
  }

  debug(...messages: unknown[]): void {
    console.debug(`[${this.name}]`, ...messages);
    sendSentryLog("debug", this.name, messages);
  }

  error(...messages: unknown[]): void {
    console.error(`[${this.name}]`, ...messages);
    sendSentryLog("error", this.name, messages);
  }

  warn(...messages: unknown[]): void {
    console.warn(`[${this.name}]`, ...messages);
    sendSentryLog("warn", this.name, messages);
  }

  dir(data: unknown): void {
    console.info(`[${this.name}]`);
    console.dir(data);
    sendSentryLog("debug", this.name, ["dir", { data }]);
  }

  exception(exception: unknown): void {
    console.error(`[${this.name}]`, exception);
    sendSentryLog("error", this.name, [
      "exception",
      { exception: toSentryAttributeValue(exception) }
    ]);
  }
}
